import { useState, useCallback } from "react";
import { db } from "../services/firebase";
import { ref, set, push } from "firebase/database";

/**
 * ✅ Custom hook to create or update a pack in Firebase.
 * - Creates new pack under /Packs/{domainId}/
 * - Updates existing pack if packId provided
 */
export default function useCreatePack() {
    const [creatingPack, setCreatingPack] = useState(false);
    const [creatingPackError, setCreatingPackError] = useState(null);

    const createPack = useCallback(
        async (
            name,
            mrp,
            price,
            coupon,
            validity,   // validity in days
            domainId,
            packId = null,
            orderId = null
        ) => {
            // ✅ Input Validation
            if (!name?.trim()) {
                setCreatingPackError("Please enter a valid pack name");
                return false;
            }

            if (!mrp?.trim() || isNaN(mrp)) {
                setCreatingPackError("Please enter a valid numeric MRP");
                return false;
            }

            if (!price?.trim() || isNaN(price)) {
                setCreatingPackError("Please enter a valid numeric price");
                return false;
            }

            if (!coupon?.trim()) {
                setCreatingPackError("Please enter a valid coupon");
                return false;
            }

            if (!validity?.trim() || isNaN(validity)) {
                setCreatingPackError("Please enter a valid numeric validity (in days)");
                return false;
            }

            if (!domainId) {
                setCreatingPackError("Domain ID is required");
                return false;
            }

            try {
                setCreatingPack(true);
                setCreatingPackError(null);

                let packRef;

                if (packId) {
                    // 🔹 Update existing pack
                    packRef = ref(db, `Packs/${domainId}/${packId}`);
                } else {
                    // 🔹 Create new pack under domain
                    packRef = push(ref(db, `Packs/${domainId}`));
                    packId = packRef.key; // auto-generated Firebase ID
                }

                // ✅ Pack data (always numeric where needed)
                const packData = {
                    packId,
                    name: name.trim(),
                    mrp: parseFloat(mrp),
                    price: parseFloat(price),
                    coupon: coupon.trim(),
                    validity: parseInt(validity, 10), // store with clear name
                    domainId,
                    order: orderId ?? Date.now(), // fallback order
                    createdAt: Date.now(),
                };

                await set(packRef, packData);

                return true;
            } catch (err) {
                console.error("Error creating/updating pack:", err);
                setCreatingPackError("Failed to create or update pack. Try again.");
                return false;
            } finally {
                setCreatingPack(false);
            }
        },
        []
    );

    return { createPack, creatingPack, creatingPackError };
}
